import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, getDoc, orderBy, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';
import './TShirtOrderForm.css';
import StockAllocationModal from './StockAllocationModal';

function TShirtOrderForm({ onClose, onOrderPlaced }) {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('readyToOrder'); // 'readyToOrder' or 'alreadyOrdered'
  
  // Stock allocation state
  const [showStockModal, setShowStockModal] = useState(false);
  const [hasCheckedStock, setHasCheckedStock] = useState(false);
  const [allocatedStudentIds, setAllocatedStudentIds] = useState([]);
  
  // Ready to Order students
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState({});
  const [preFilledSizes, setPreFilledSizes] = useState({}); // Track which sizes were pre-filled from student records
  
  // Already Ordered students
  const [orderedStudents, setOrderedStudents] = useState([]);
  
  const [adultShirts, setAdultShirts] = useState({
    S: 0, M: 0, L: 0,
    XL: 0, XXL: 0, XXXL: 0
  });
  const [stickerRolls, setStickerRolls] = useState(0);
  
  // Extra stock state - same sizes as student sizes
  const [extraStock, setExtraStock] = useState({
    '2-3 YEARS': 0,
    '4-5 YEARS': 0,
    '5-6 YEARS': 0,
    '7-8 YEARS': 0,
    '9-10 YEARS': 0,
    '11-12 YEARS': 0,
    '13-14 YEARS': 0,
    'S': 0,
    'M': 0,
    'L': 0
  });
  const [adminFeeStructure, setAdminFeeStructure] = useState(null);
  const [franchiseeName, setFranchiseeName] = useState('');
  const [includeInMonthEnd, setIncludeInMonthEnd] = useState(true); // Default to true
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]); // Order date picker

  const tshirtSizes = [
    '2-3 YEARS',
    '4-5 YEARS',
    '5-6 YEARS',
    '7-8 YEARS',
    '9-10 YEARS',
    '11-12 YEARS',
    '13-14 YEARS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'XXXL'
  ];

  // Student T-shirt sizes - kids sizes up to L only (no XL, XXL, XXXL)
  const studentSizes = [
    '2-3 YEARS',
    '4-5 YEARS',
    '5-6 YEARS',
    '7-8 YEARS',
    '9-10 YEARS',
    '11-12 YEARS',
    '13-14 YEARS',
    'S',
    'M',
    'L'
  ];

  // Extra stock sizes - same as student sizes (includes S, M, L)
  const extraStockSizes = [
    '2-3 YEARS',
    '4-5 YEARS',
    '5-6 YEARS',
    '7-8 YEARS',
    '9-10 YEARS',
    '11-12 YEARS',
    '13-14 YEARS',
    'S',
    'M',
    'L'
  ];

  useEffect(() => {
    checkForStock();
  }, []);

  const checkForStock = async () => {
    setLoadingData(true);
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      console.log('Ã°Å¸â€Â Checking for stock...');
      console.log('Franchisee UID:', user.uid);
      console.log('Franchisee Ref:', franchiseeRef.path);
      
      // Check if there's available stock
      const stockQuery = query(
        collection(db, 'tshirtStock'),
        where('franchiseeRef', '==', franchiseeRef),
        where('remaining', '>', 0)
      );
      
      const stockSnapshot = await getDocs(stockQuery);
      
      console.log('Ã°Å¸â€œÂ¦ Stock found:', stockSnapshot.size);
      
      if (stockSnapshot.size > 0) {
        console.log('Ã¢Å“â€¦ Stock exists! Showing allocation modal...');
        stockSnapshot.forEach(doc => {
          console.log('  - Stock item:', doc.data());
        });
        
        // Stock exists - show allocation modal
        setShowStockModal(true);
        setLoadingData(false);
      } else {
        console.log('Ã¢ÂÅ’ No stock found. Proceeding to order form...');
        // No stock - proceed to normal order form
        setHasCheckedStock(true);
        await loadAllData();
      }
    } catch (error) {
      console.error('Ã¢ÂÅ’ Error checking stock:', error);
      // On error, proceed to normal order form
      setHasCheckedStock(true);
      await loadAllData();
    }
  };

  const loadAllData = async () => {
    setLoadingData(true);
    try {
      await Promise.all([
        loadEligibleStudents(),
        loadOrderedStudents(),
        loadAdminFeeStructure(),
        loadFranchiseeName()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please try again.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleStockAllocationComplete = (allocatedIds) => {
    // Store allocated student IDs to filter them out
    setAllocatedStudentIds(allocatedIds);
  };

  const handleContinueToOrder = async () => {
    // Close stock modal and load order form
    setShowStockModal(false);
    setHasCheckedStock(true);
    await loadAllData();
  };

  const handleSkipStockAllocation = async () => {
    // Skip stock allocation and go straight to order form
    setShowStockModal(false);
    setHasCheckedStock(true);
    await loadAllData();
  };

  const loadFranchiseeName = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeDoc = await getDoc(doc(db, 'Franchisees', user.uid));
      if (franchiseeDoc.exists()) {
        setFranchiseeName(franchiseeDoc.data().name || 'Unknown');
      }
    } catch (error) {
      console.error('Error loading franchisee name:', error);
    }
  };

  const loadEligibleStudents = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      // Query active students (filter tshirtOrdered client-side to handle missing field)
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const students = [];
      const initialSizes = {}; // Store pre-existing sizes
      
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        
        // Skip if T-shirt already ordered (check client-side to handle missing field)
        if (data.tshirtOrdered === true) {
          return;
        }
        
        // Skip if already allocated in this session
        if (allocatedStudentIds.includes(docSnap.id)) {
          return;
        }
        
        // Check if T-shirt is included:
        // - Either adminFeeBreakdown.tshirtIncluded is true (from app enrollment)
        // - OR adminFee is 160 (standard fee that includes T-shirt, from WordPress)
        const tshirtIncluded = data.adminFeeBreakdown?.tshirtIncluded || data.adminFee === 160;
        
        if (tshirtIncluded) {
          const adminFee = data.adminFee || 0;
          
          // Get admin fee paid from termPayments.admin (correct field!)
          const adminFeePaid = data.termPayments?.admin || 0;
          
          // Only include if admin fee is fully paid
          if (adminFeePaid >= adminFee) {
            students.push({
              id: docSnap.id,
              ...data
            });
            
            // Pre-populate size if it exists in student record
            if (data.tShirtSize) {
              // Normalize size format: ensure it matches dropdown options
              let normalizedSize = data.tShirtSize;
              
              // If size doesn't have "YEARS" suffix and is a child size, add it
              if (!normalizedSize.includes('YEARS') && 
                  !['S', 'M', 'L', 'XL', 'XXL', 'XXXL'].includes(normalizedSize)) {
                normalizedSize = `${normalizedSize} YEARS`;
              }
              
              // Check if this normalized size exists in our dropdown options
              if (tshirtSizes.includes(normalizedSize)) {
                initialSizes[docSnap.id] = normalizedSize;
              } else {
                // If exact match not found, use the original value
                initialSizes[docSnap.id] = data.tShirtSize;
              }
            }
          }
        }
      });
      
      students.sort((a, b) => {
        if (a.schoolName !== b.schoolName) {
          return a.schoolName.localeCompare(b.schoolName);
        }
        return a.studentName.localeCompare(b.studentName);
      });
      
      setEligibleStudents(students);
      
      // Pre-populate selectedSizes with existing sizes from student records
      setSelectedSizes(initialSizes);
      
      // Track which sizes were pre-filled (for visual indicator)
      setPreFilledSizes(initialSizes);
    } catch (error) {
      console.error('Error loading eligible students:', error);
      throw error;
    }
  };

  const loadOrderedStudents = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const q = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef),
        where('tshirtOrdered', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const students = [];
      
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        students.push({
          id: docSnap.id,
          ...data
        });
      });
      
      students.sort((a, b) => {
        // Sort by order date (most recent first)
        const dateA = a.tshirtOrderDate?.toDate ? a.tshirtOrderDate.toDate() : new Date(0);
        const dateB = b.tshirtOrderDate?.toDate ? b.tshirtOrderDate.toDate() : new Date(0);
        return dateB - dateA;
      });
      
      setOrderedStudents(students);
    } catch (error) {
      console.error('Error loading ordered students:', error);
      throw error;
    }
  };

  const loadAdminFeeStructure = async () => {
    try {
      const adminFeeDoc = await getDoc(doc(db, 'adminFeeStructure', 'current'));
      
      if (adminFeeDoc.exists()) {
        setAdminFeeStructure(adminFeeDoc.data());
      } else {
        setAdminFeeStructure({
          adultTshirtPricing: {
            smallToLarge: 200,
            xlToXxxxl: 200
          },
          tshirtCostToHO: 110,
          stickerPricePerRoll: 70
        });
      }
    } catch (error) {
      console.error('Error loading admin fee structure:', error);
      throw error;
    }
  };

  const handleSizeChange = (studentId, size) => {
    setSelectedSizes(prev => ({
      ...prev,
      [studentId]: size
    }));
    
    // Remove pre-filled indicator once manually changed
    if (preFilledSizes[studentId]) {
      setPreFilledSizes(prev => {
        const updated = { ...prev };
        delete updated[studentId];
        return updated;
      });
    }
  };

  const handleAdultShirtChange = (size, value) => {
    const quantity = parseInt(value) || 0;
    setAdultShirts(prev => ({
      ...prev,
      [size]: quantity >= 0 ? quantity : 0
    }));
  };

  const handleStickerChange = (value) => {
    const quantity = parseInt(value) || 0;
    setStickerRolls(quantity >= 0 ? quantity : 0);
  };

  const handleExtraStockChange = (size, value) => {
    const quantity = parseInt(value) || 0;
    setExtraStock(prev => ({
      ...prev,
      [size]: quantity >= 0 ? quantity : 0
    }));
  };

  const calculateTotals = () => {
    if (!adminFeeStructure) {
      return {
        studentCount: 0,
        adultCount: 0,
        totalTshirts: 0,
        adultTotal: 0,
        stickerTotal: 0,
        studentTshirtCost: 0,
        extraStockCount: 0,
        extraStockCost: 0,
        totalCost: 0,
        costToHO: 0
      };
    }

    const studentCount = Object.keys(selectedSizes).length;
    const smallToLargeCount = adultShirts.S + adultShirts.M + adultShirts.L;
    const xlToXxxxlCount = adultShirts.XL + adultShirts.XXL + adultShirts.XXXL;
    const adultCount = smallToLargeCount + xlToXxxxlCount;
    
    // Calculate extra stock
    const extraStockCount = Object.values(extraStock).reduce((sum, qty) => sum + qty, 0);
    const extraStockCost = extraStockCount * 120; // R120 per extra stock shirt
    
    const totalTshirts = studentCount + adultCount + extraStockCount;

    // Student T-shirt cost (R120 from admin fee for each student)
    const studentTshirtCost = studentCount * 120;

    // Adult T-shirt cost (what franchisee pays for adult shirts)
    const adultTotal = 
      (smallToLargeCount * adminFeeStructure.adultTshirtPricing.smallToLarge) +
      (xlToXxxxlCount * adminFeeStructure.adultTshirtPricing.xlToXxxxl);

    // Sticker cost
    const stickerTotal = stickerRolls * adminFeeStructure.stickerPricePerRoll;
    
    // TOTAL franchisee owes: Student T-shirt portion + Adult shirts + Stickers + Extra Stock
    const totalCost = studentTshirtCost + adultTotal + stickerTotal + extraStockCost;
    
    // Total cost to HO for procurement (all T-shirts at R110 each)
    const costToHO = totalTshirts * adminFeeStructure.tshirtCostToHO;

    return {
      studentCount,
      adultCount,
      totalTshirts,
      adultTotal,
      stickerTotal,
      studentTshirtCost,
      extraStockCount,
      extraStockCost,
      totalCost,
      costToHO
    };
  };

  const generateOrderNumber = async () => {
    try {
      const selectedDate = new Date(orderDate);
      const year = selectedDate.getFullYear();
      const startOfYear = new Date(year, 0, 1);
      
      const q = query(
        collection(db, 'tshirtOrders'),
        where('orderDate', '>=', Timestamp.fromDate(startOfYear))
      );
      
      const snapshot = await getDocs(q);
      const count = snapshot.size + 1;
      
      return `TS-${year}-${String(count).padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating order number:', error);
      return `TS-${Date.now()}`;
    }
  };

  const validateOrder = () => {
    const totals = calculateTotals();
    
    if (totals.studentCount === 0 && totals.adultCount === 0 && stickerRolls === 0 && totals.extraStockCount === 0) {
      alert('Please select at least one item to order.');
      return false;
    }

    const studentsWithoutSizes = eligibleStudents.filter(student => 
      selectedSizes[student.id] === undefined || selectedSizes[student.id] === ''
    );

    if (studentsWithoutSizes.length > 0 && totals.studentCount > 0) {
      alert(`Please select sizes for all students before submitting. ${studentsWithoutSizes.length} students need sizes.`);
      return false;
    }

    return true;
  };

  const handleSubmitOrder = async () => {
    if (!validateOrder()) {
      return;
    }

    const totals = calculateTotals();
    
    let confirmMessage = `Confirm Order:\n- Student T-shirts: ${totals.studentCount}\n- Adult T-shirts: ${totals.adultCount}`;
    
    // Show individual adult size breakdown
    const adultSizeBreakdown = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL']
      .filter(size => adultShirts[size] > 0)
      .map(size => `${size}: ${adultShirts[size]}`)
      .join(', ');
    if (adultSizeBreakdown) {
      confirmMessage += ` (${adultSizeBreakdown})`;
    }
    
    confirmMessage += `\n- Sticker rolls: ${stickerRolls}`;
    
    if (totals.extraStockCount > 0) {
      confirmMessage += `\n- Extra Stock: ${totals.extraStockCount} shirts (R${totals.extraStockCost.toFixed(2)})`;
    }
    
    confirmMessage += `\n- Total Cost: R${totals.totalCost.toFixed(2)}\n\nSubmit this order?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const orderNumber = await generateOrderNumber();

      const studentTshirts = eligibleStudents
        .filter(student => selectedSizes[student.id])
        .map(student => ({
          studentRef: doc(db, 'students', student.id),
          studentName: student.studentName,
          schoolName: student.schoolName,
          size: selectedSizes[student.id],
          adminFeeAmount: student.adminFee || 0,
          cost: 0
        }));

      const smallToLargeCount = adultShirts.S + adultShirts.M + adultShirts.L;
      const xlToXxxxlCount = adultShirts.XL + adultShirts.XXL + adultShirts.XXXL;

      const orderData = {
        orderNumber: orderNumber,
        orderDate: new Date(orderDate),
        franchiseeRef: franchiseeRef,
        franchiseeName: franchiseeName,
        studentTshirts: studentTshirts,
        adultTshirts: {
          smallToLarge: {
            quantity: smallToLargeCount,
            sizes: { S: adultShirts.S, M: adultShirts.M, L: adultShirts.L },
            priceEach: adminFeeStructure.adultTshirtPricing.smallToLarge,
            total: smallToLargeCount * adminFeeStructure.adultTshirtPricing.smallToLarge
          },
          xlToXxxxl: {
            quantity: xlToXxxxlCount,
            sizes: { XL: adultShirts.XL, XXL: adultShirts.XXL, XXXL: adultShirts.XXXL },
            priceEach: adminFeeStructure.adultTshirtPricing.xlToXxxxl,
            total: xlToXxxxlCount * adminFeeStructure.adultTshirtPricing.xlToXxxxl
          }
        },
        stickers: {
          quantity: stickerRolls,
          pricePerRoll: adminFeeStructure.stickerPricePerRoll,
          total: stickerRolls * adminFeeStructure.stickerPricePerRoll
        },
        extraStock: extraStock,
        extraStockCount: totals.extraStockCount,
        extraStockCost: totals.extraStockCost,
        studentTshirtCount: totals.studentCount,
        adultTshirtTotal: totals.adultTotal,
        stickerTotal: totals.stickerTotal,
        totalCost: totals.totalCost,
        tshirtCostToHO: adminFeeStructure.tshirtCostToHO,
        totalTshirts: totals.totalTshirts,
        totalCostToHO: totals.costToHO,
        status: 'pending',
        shipped: false,
        shippedDate: null,
        deliveredDate: null,
        notes: '',
        includeInMonthEnd: includeInMonthEnd,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const orderRef = await addDoc(collection(db, 'tshirtOrders'), orderData);

      const updatePromises = studentTshirts.map(student => 
        updateDoc(student.studentRef, {
          tshirtOrdered: true,
          tshirtOrderId: orderRef.id,
          tshirtOrderDate: new Date(orderDate),
          tShirtSize: student.size,
          updatedAt: new Date()
        })
      );

      await Promise.all(updatePromises);

      // Create stock documents for extra stock items
      const stockPromises = [];
      for (const [size, quantity] of Object.entries(extraStock)) {
        if (quantity > 0) {
          const stockData = {
            franchiseeRef: franchiseeRef,
            franchiseeName: franchiseeName,
            size: size,
            totalOrdered: quantity,
            allocated: 0,
            remaining: quantity,
            sourceOrderId: orderRef.id,
            sourceOrderNumber: orderNumber,
            sourceOrderDate: new Date(orderDate),
            allocations: [],
            createdAt: new Date(),
            updatedAt: new Date()
          };
          stockPromises.push(addDoc(collection(db, 'tshirtStock'), stockData));
        }
      }
      
      if (stockPromises.length > 0) {
        await Promise.all(stockPromises);
      }

      let successMessage = `Order ${orderNumber} placed successfully!\n\nStudent T-shirts: ${totals.studentCount}\nAdult T-shirts: ${totals.adultCount}`;
      
      // Show individual adult size breakdown in success message
      const successSizeBreakdown = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL']
        .filter(size => adultShirts[size] > 0)
        .map(size => `  ${size}: ${adultShirts[size]}`)
        .join('\n');
      if (successSizeBreakdown) {
        successMessage += `\n${successSizeBreakdown}`;
      }
      
      successMessage += `\nStickers: ${stickerRolls} rolls`;
      
      if (totals.extraStockCount > 0) {
        successMessage += `\nExtra Stock: ${totals.extraStockCount} shirts`;
      }
      
      successMessage += `\n\nTotal: R${totals.totalCost.toFixed(2)}`;
      
      alert(successMessage);

      if (onOrderPlaced) {
        onOrderPlaced();
      }

      onClose();

    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Error submitting order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();
const exportOrderDetails = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      const ordersQuery = query(
        collection(db, 'tshirtOrders'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      
      const snapshot = await getDocs(ordersQuery);
      
      if (snapshot.docs.length === 0) {
        alert('No orders found.');
        return;
      }
      
      let csv = `T-SHIRT ORDER DETAILS\n`;
      csv += `Franchisee: ${franchiseeName}\n`;
      csv += `Generated: ${new Date().toLocaleDateString('en-GB')}\n`;
      
      snapshot.docs.forEach(docSnap => {
        const order = docSnap.data();
        const orderDate = order.orderDate?.toDate ? order.orderDate.toDate().toLocaleDateString('en-GB') : 'N/A';
        
        csv += `\n========================================\n`;
        csv += `ORDER: ${order.orderNumber || 'N/A'}\n`;
        csv += `Date: ${orderDate}\n`;
        csv += `========================================\n`;
        
        // Student Shirts - sorted by School then Student Name
        const students = order.studentTshirts || [];
        if (students.length > 0) {
          const sortedStudents = [...students].sort((a, b) => {
            const schoolCompare = (a.schoolName || '').localeCompare(b.schoolName || '');
            if (schoolCompare !== 0) return schoolCompare;
            return (a.studentName || '').localeCompare(b.studentName || '');
          });
          
          csv += `\nSTUDENT SHIRTS (${students.length})\n`;
          csv += `School,Student Name,Size\n`;
          sortedStudents.forEach(s => {
            csv += `${s.schoolName || 'N/A'},${s.studentName || 'N/A'},${s.size || 'N/A'}\n`;
          });
        }
        
        // Extra Stock
        const extras = order.extraStock || {};
        const hasExtras = Object.values(extras).some(qty => qty > 0);
        if (hasExtras) {
          csv += `\nEXTRA STOCK\n`;
          csv += `Size,Quantity\n`;
          Object.entries(extras).forEach(([size, qty]) => {
            if (qty > 0) csv += `${size},${qty}\n`;
          });
        }
        
        // Adult Shirts
        const adultSL = order.adultTshirts?.smallToLarge?.sizes || {};
        const adultXL = order.adultTshirts?.xlToXxxxl?.sizes || {};
        const allAdult = { ...adultSL, ...adultXL };
        const hasAdult = Object.values(allAdult).some(qty => qty > 0);
        if (hasAdult) {
          csv += `\nADULT SHIRTS\n`;
          csv += `Size,Quantity\n`;
          Object.entries(allAdult).forEach(([size, qty]) => {
            if (qty > 0) csv += `${size},${qty}\n`;
          });
        }
        
        // Stickers
        const stickerQty = order.stickers?.quantity || 0;
        if (stickerQty > 0) {
          csv += `\nSTICKERS\n`;
          csv += `Rolls,${stickerQty}\n`;
        }
        
        // Size Summary (students + extra stock combined)
        const allSizesOrdered = ['2-3 YEARS', '4-5 YEARS', '5-6 YEARS', '7-8 YEARS', '9-10 YEARS', '11-12 YEARS', '13-14 YEARS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
        const sizeTotals = {};
        students.forEach(s => {
          if (s.size) sizeTotals[s.size] = (sizeTotals[s.size] || 0) + 1;
        });
        Object.entries(extras).forEach(([size, qty]) => {
          if (qty > 0) sizeTotals[size] = (sizeTotals[size] || 0) + qty;
        });
        
        const sizesWithCount = allSizesOrdered.filter(size => sizeTotals[size] > 0);
        if (sizesWithCount.length > 0) {
          csv += `\nSIZE SUMMARY (for checking when you receive shirts)\n`;
          csv += `Size,Quantity\n`;
          sizesWithCount.forEach(size => {
            csv += `${size},${sizeTotals[size]}\n`;
          });
          const totalShirts = sizesWithCount.reduce((sum, size) => sum + sizeTotals[size], 0);
          csv += `TOTAL ROUND NECK SHIRTS,${totalShirts}\n`;
          
          // V-neck summary if any
          const vneckSizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
          const vneckTotals = {};
          vneckSizes.forEach(size => {
            const qty = (adultSL[size] || 0) + (adultXL[size] || 0);
            if (qty > 0) vneckTotals[size] = qty;
          });
          if (Object.keys(vneckTotals).length > 0) {
            const totalVneck = Object.values(vneckTotals).reduce((s, v) => s + v, 0);
            csv += `TOTAL V-NECK SHIRTS,${totalVneck}\n`;
          }
        }
        
        // Cost Summary
        csv += `\nCOST SUMMARY\n`;
        csv += `Student Shirts,${students.length}\n`;
        csv += `Extra Stock Cost,R${(order.extraStockCost || 0).toFixed(2)}\n`;
        csv += `Adult Shirt Cost,R${((order.adultTshirts?.smallToLarge?.total || 0) + (order.adultTshirts?.xlToXxxxl?.total || 0)).toFixed(2)}\n`;
        csv += `Sticker Cost,R${(order.stickers?.total || 0).toFixed(2)}\n`;
        csv += `Total Cost,R${(order.totalCost || 0).toFixed(2)}\n`;
      });
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TShirtOrderDetails_${franchiseeName.replace(/\s/g, '_')}.csv`;
      a.click();
    } catch (error) {
      console.error('Error exporting order details:', error);
      alert('Error exporting: ' + error.message);
    }
  };

  // Show stock allocation modal if stock exists and hasn't been checked yet
  if (showStockModal && !hasCheckedStock) {
    return (
      <StockAllocationModal
        onClose={onClose}
        onContinueToOrder={handleContinueToOrder}
        onAllocationComplete={handleStockAllocationComplete}
      />
    );
  }

  if (loadingData) {
    return (
      <div className="tshirt-order-overlay">
        <div className="tshirt-order-container">
          <div className="loading">Loading T-shirt order form...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="tshirt-order-overlay">
      <div className="tshirt-order-container">
        <div className="tshirt-order-header">
          <h1>Order T-Shirts & Supplies</h1>
          <button onClick={onClose} className="btn-close" disabled={loading}>X</button>
        </div>

        {/* TAB NAVIGATION */}
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'readyToOrder' ? 'active' : ''}`}
            onClick={() => setActiveTab('readyToOrder')}
          >
            <span className="tab-icon">🛒</span>
            <span className="tab-label">Ready to Order</span>
            <span className="tab-count">{eligibleStudents.length}</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'alreadyOrdered' ? 'active' : ''}`}
            onClick={() => setActiveTab('alreadyOrdered')}
          >
            <span className="tab-icon">✔</span>
            <span className="tab-label">Already Ordered</span>
            <span className="tab-count">{orderedStudents.length}</span>
          </button>
        </div>

        <div className="tshirt-order-content">
          {/* TAB 1: READY TO ORDER */}
          {activeTab === 'readyToOrder' && (
            <>
              {/* STUDENT T-SHIRTS */}
              <div className="order-section">
                <h2>
                  Students with Paid Admin Fees
                  {eligibleStudents.length > 0 && (
                    <span className="count-badge">{eligibleStudents.length} ready</span>
                  )}
                </h2>

                {eligibleStudents.length === 0 ? (
                  <div className="empty-state">
                    <h3>No Students Ready for T-Shirt Orders</h3>
                    <p>
                      Students will appear here once they:
                      <br/>• Have R160 admin fee (T-shirt included)
                      <br/>• Have paid their admin fee in full
                      <br/>• Haven't been ordered yet
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                      Select T-shirt sizes for students below. Sizes are automatically filled from student records but can be changed if needed. Student T-shirts are included in the admin fee (no additional cost).
                    </div>

                    <table className="student-table">
                      <thead>
                        <tr>
                          <th>Student Name</th>
                          <th>School</th>
                          <th>Admin Fee Paid</th>
                          <th>T-Shirt Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eligibleStudents.map(student => (
                          <tr key={student.id}>
                            <td>{student.studentName}</td>
                            <td>{student.schoolName}</td>
                            <td style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                              ✅ R{student.termPayments?.admin || 0}
                            </td>
                            <td>
                              <select
                                className={`size-select ${selectedSizes[student.id] ? 'selected' : ''}`}
                                value={selectedSizes[student.id] || ''}
                                onChange={(e) => handleSizeChange(student.id, e.target.value)}
                              >
                                <option value="">-- Select Size --</option>
                                {studentSizes.map(size => (
                                  <option key={size} value={size}>
                                    {size}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div style={{ 
                      marginTop: '16px', 
                      padding: '12px', 
                      background: '#e7f3ff', 
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#1976d2'
                    }}>
                      <strong>Selected:</strong> {totals.studentCount} of {eligibleStudents.length} students
                    </div>
                  </>
                )}
              </div>

              {/* ADULT T-SHIRTS */}
              {adminFeeStructure && (
                <div className="order-section">
                  <h2>Adult T-Shirts (Optional)</h2>
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                    Order T-shirts for yourself, staff, or family members. Select the quantity for each size.
                  </div>

                  {/* Small to Large Group */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                      padding: '10px 14px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      color: 'white'
                    }}>
                      <span style={{ fontWeight: '600', fontSize: '15px' }}>Small – Large</span>
                      <span style={{ 
                        background: 'rgba(255,255,255,0.25)', 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        R{adminFeeStructure.adultTshirtPricing.smallToLarge} each
                      </span>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '12px' 
                    }}>
                      {['S', 'M', 'L'].map(size => (
                        <div key={size} style={{ 
                          padding: '12px', 
                          border: '2px solid #e0e0e0', 
                          borderRadius: '8px',
                          background: adultShirts[size] > 0 ? '#f0f0ff' : 'white',
                          borderColor: adultShirts[size] > 0 ? '#667eea' : '#e0e0e0',
                          textAlign: 'center'
                        }}>
                          <label style={{ 
                            display: 'block', 
                            fontWeight: '700', 
                            marginBottom: '8px',
                            fontSize: '16px',
                            color: '#333'
                          }}>
                            {size}
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={adultShirts[size]}
                            onChange={(e) => handleAdultShirtChange(size, e.target.value)}
                            placeholder="0"
                            style={{
                              width: '100%',
                              padding: '10px',
                              fontSize: '18px',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontWeight: '600'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {(adultShirts.S + adultShirts.M + adultShirts.L) > 0 && (
                      <div style={{ marginTop: '10px', fontSize: '14px', color: '#4CAF50', fontWeight: 'bold', textAlign: 'right' }}>
                        Subtotal: {adultShirts.S + adultShirts.M + adultShirts.L} × R{adminFeeStructure.adultTshirtPricing.smallToLarge} = R{((adultShirts.S + adultShirts.M + adultShirts.L) * adminFeeStructure.adultTshirtPricing.smallToLarge).toFixed(2)}
                      </div>
                    )}
                  </div>

                  {/* XL to XXXL Group */}
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                      padding: '10px 14px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '8px',
                      color: 'white'
                    }}>
                      <span style={{ fontWeight: '600', fontSize: '15px' }}>XL – XXXL</span>
                      <span style={{ 
                        background: 'rgba(255,255,255,0.25)', 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        R{adminFeeStructure.adultTshirtPricing.xlToXxxxl} each
                      </span>
                    </div>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '12px' 
                    }}>
                      {['XL', 'XXL', 'XXXL'].map(size => (
                        <div key={size} style={{ 
                          padding: '12px', 
                          border: '2px solid #e0e0e0', 
                          borderRadius: '8px',
                          background: adultShirts[size] > 0 ? '#f0f0ff' : 'white',
                          borderColor: adultShirts[size] > 0 ? '#667eea' : '#e0e0e0',
                          textAlign: 'center'
                        }}>
                          <label style={{ 
                            display: 'block', 
                            fontWeight: '700', 
                            marginBottom: '8px',
                            fontSize: '16px',
                            color: '#333'
                          }}>
                            {size}
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={adultShirts[size]}
                            onChange={(e) => handleAdultShirtChange(size, e.target.value)}
                            placeholder="0"
                            style={{
                              width: '100%',
                              padding: '10px',
                              fontSize: '18px',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontWeight: '600'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {(adultShirts.XL + adultShirts.XXL + adultShirts.XXXL) > 0 && (
                      <div style={{ marginTop: '10px', fontSize: '14px', color: '#4CAF50', fontWeight: 'bold', textAlign: 'right' }}>
                        Subtotal: {adultShirts.XL + adultShirts.XXL + adultShirts.XXXL} × R{adminFeeStructure.adultTshirtPricing.xlToXxxxl} = R{((adultShirts.XL + adultShirts.XXL + adultShirts.XXXL) * adminFeeStructure.adultTshirtPricing.xlToXxxxl).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STICKERS */}
              {adminFeeStructure && (
                <div className="order-section">
                  <h2>Sticker Rolls (Optional)</h2>
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                    Order sticker rolls for rewards and motivation.
                  </div>

                  <div className="product-grid">
                    <div className="product-item">
                      <label>Sticker Rolls</label>
                      <span className="price-tag">R{adminFeeStructure.stickerPricePerRoll} per roll</span>
                      <input
                        type="number"
                        min="0"
                        value={stickerRolls}
                        onChange={(e) => handleStickerChange(e.target.value)}
                        placeholder="Number of rolls"
                      />
                      {stickerRolls > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '14px', color: '#4CAF50', fontWeight: 'bold' }}>
                          Subtotal: R{(stickerRolls * adminFeeStructure.stickerPricePerRoll).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* EXTRA STOCK */}
              {adminFeeStructure && (
                <div className="order-section">
                  <h2>
                    
                     Extra Stock (Optional)</h2>
                  <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
                    Order additional T-shirts to keep in stock for future students. These will be available for allocation when new students pay their admin fees.
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                    gap: '16px' 
                  }}>
                    {extraStockSizes.map(size => (
                      <div key={size} style={{ 
                        padding: '12px', 
                        border: '2px solid #e0e0e0', 
                        borderRadius: '8px',
                        background: extraStock[size] > 0 ? '#f0f7ff' : 'white',
                        borderColor: extraStock[size] > 0 ? '#2196F3' : '#e0e0e0'
                      }}>
                        <label style={{ 
                          display: 'block', 
                          fontWeight: '600', 
                          marginBottom: '8px',
                          fontSize: '14px'
                        }}>
                          {size}
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={extraStock[size]}
                          onChange={(e) => handleExtraStockChange(size, e.target.value)}
                          placeholder="0"
                          style={{
                            width: '100%',
                            padding: '8px',
                            fontSize: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                        />
                        {extraStock[size] > 0 && (
                          <div style={{ 
                            marginTop: '8px', 
                            fontSize: '12px', 
                            color: '#2196F3', 
                            fontWeight: 'bold' 
                          }}>
                            R{(extraStock[size] * 120).toFixed(2)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {totals.extraStockCount > 0 && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: '#e3f2fd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: '#1976d2',
                      fontWeight: '600'
                    }}>
                      Extra Stock Total: {totals.extraStockCount} shirts × R120 = R{totals.extraStockCost.toFixed(2)}
                    </div>
                  )}
                </div>
              )}

              {/* ORDER SUMMARY */}
              {(totals.studentCount > 0 || totals.adultCount > 0 || stickerRolls > 0 || totals.extraStockCount > 0) && (
                <div className="order-summary">
                  <h2>Order Summary</h2>

                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Student T-Shirts</label>
                      <div className="value">{totals.studentCount}</div>
                      <small>R120 each (from admin fee)</small>
                    </div>

                    <div className="summary-item">
                      <label>Adult T-Shirts</label>
                      <div className="value">{totals.adultCount}</div>
                      <small>R{totals.adultTotal.toFixed(2)}</small>
                    </div>

                    <div className="summary-item">
                      <label>Sticker Rolls</label>
                      <div className="value">{stickerRolls}</div>
                      <small>R{totals.stickerTotal.toFixed(2)}</small>
                    </div>

                    {totals.extraStockCount > 0 && (
                      <div className="summary-item">
                        <label>Extra Stock</label>
                        <div className="value">{totals.extraStockCount}</div>
                        <small>R{totals.extraStockCost.toFixed(2)}</small>
                      </div>
                    )}

                    <div className="summary-item">
                      <label>Total T-Shirts to Order</label>
                      <div className="value">{totals.totalTshirts}</div>
                      <small>HO will procure at R{totals.costToHO.toFixed(2)}</small>
                    </div>
                  </div>

                  <div style={{
                    background: 'rgba(102, 126, 234, 0.1)',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#333' }}>
                      <span>Student T-shirts ({totals.studentCount} × R120):</span>
                      <strong>R{totals.studentTshirtCost.toFixed(2)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#333' }}>
                      <span>Adult T-shirts:</span>
                      <strong>R{totals.adultTotal.toFixed(2)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#333' }}>
                      <span>Stickers:</span>
                      <strong>R{totals.stickerTotal.toFixed(2)}</strong>
                    </div>
                    {totals.extraStockCount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#333' }}>
                        <span>Extra Stock ({totals.extraStockCount} × R120):</span>
                        <strong>R{totals.extraStockCost.toFixed(2)}</strong>
                      </div>
                    )}
                  </div>

                  <div className="summary-total">
                    <label>Total Due to Head Office:</label>
                    <div className="value">R{totals.totalCost.toFixed(2)}</div>
                  </div>

                  {/* Order Date Picker */}
                  <div className="order-date-picker" style={{
                    background: 'rgba(102, 126, 234, 0.1)',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    marginTop: '16px'
                  }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                      📅 Order Date
                    </label>
                    <input
                      type="date"
                      value={orderDate}
                      onChange={(e) => setOrderDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '2px solid #667eea',
                        fontSize: '16px'
                      }}
                    />
                    <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                      Set the date for this T-shirt order
                    </small>
                  </div>

                  {/* Include in Month-End Toggle */}
                  <div className="month-end-toggle">
                    <div className="toggle-container">
                      <label className="toggle-switch">
                        <input 
                          type="checkbox" 
                          checked={includeInMonthEnd}
                          onChange={(e) => setIncludeInMonthEnd(e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <div className="toggle-label">
                        <span className="toggle-title">Include in Month-End Return</span>
                        <span className="toggle-description">
                          {includeInMonthEnd 
                            ? "This order will be added to your Summary of Deposits" 
                            : "Pay separately - will NOT be added to month-end"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="summary-note">
                    This includes R120 per student T-shirt (from admin fees collected), plus adult T-shirts and stickers ordered.
                  </div>
                </div>
              )}
            </>
          )}

          {/* TAB 2: ALREADY ORDERED */}
          {activeTab === 'alreadyOrdered' && (
            <div className="order-section">
              <h2>
                Previously Ordered Students
                {orderedStudents.length > 0 && (
                  <span className="count-badge">{orderedStudents.length} ordered</span>
                )}
              </h2>

              {orderedStudents.length === 0 ? (
                <div className="empty-state">
                  <h3>No T-Shirts Ordered Yet</h3>
                  <p>
                    Students will appear here after you place a T-shirt order.
                  </p>
                </div>
              ) : (
                <table className="student-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>School</th>
                      <th>Size Ordered</th>
                      <th>Source</th>
                      <th>Order Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedStudents.map(student => {
                      // Determine which date to display
                      let displayDate = 'N/A';
                      if (student.tshirtSource === 'stock' && student.stockAllocationDate) {
                        // For stock allocations, show allocation date
                        displayDate = student.stockAllocationDate.toDate 
                          ? student.stockAllocationDate.toDate().toLocaleDateString() 
                          : 'N/A';
                      } else if (student.tshirtOrderDate) {
                        // For regular orders, show order date
                        displayDate = student.tshirtOrderDate.toDate 
                          ? student.tshirtOrderDate.toDate().toLocaleDateString() 
                          : 'N/A';
                      }

                      // Determine source badge
                      const sourceDisplay = student.tshirtSource === 'stock' 
                        ? { label: '📦 Stock', color: '#2196F3' }
                        : { label: '🆕 New Order', color: '#4CAF50' };

                      return (
                        <tr key={student.id}>
                          <td>{student.studentName}</td>
                          <td>{student.schoolName}</td>
                          <td style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                            {student.tShirtSize || 'N/A'}
                          </td>
                          <td>
                            <span style={{ 
                              display: 'inline-block',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: 'white',
                              backgroundColor: sourceDisplay.color
                            }}>
                              {sourceDisplay.label}
                            </span>
                          </td>
                          <td>{displayDate}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div style={{ 
                marginTop: '20px', 
                padding: '16px', 
                background: '#f0f8f0', 
                borderRadius: '8px',
                fontSize: '14px',
                color: '#2e7d32'
              }}>
                <strong>ℹ️ Note:</strong> These students already have T-shirts ordered and will not appear in the "Ready to Order" tab.
              </div>
              <button 
                onClick={exportOrderDetails} 
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                📥 Export Order Details
              </button>
            </div>
          )}
        </div>

        <div className="tshirt-order-footer">
          <button onClick={onClose} className="btn-cancel" disabled={loading}>
            Cancel
          </button>
          {activeTab === 'readyToOrder' && (
            <button onClick={handleSubmitOrder} className="btn-submit" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TShirtOrderForm;